import {request} from "@utils/http";
import type { PageRequest, PageResponse } from '@types/api';

/**
 * 时间范围枚举
 */
export enum TimeRangeEnum {
    /** 今日 */
    TODAY = 'today',
    /** 本周 */
    THIS_WEEK = 'thisWeek',
    /** 本月 */
    THIS_MONTH = 'thisMonth',
    /** 本年 */
    THIS_YEAR = 'thisYear',
    /** 自定义 */
    CUSTOM = 'custom'
}

/**
 * 数据范围枚举
 */
export enum DataRangeEnum {
    /** 本人 */
    SELF = 'SELF',
    /** 本部门 */
    DEPT = 'DEPT',
    /** 本分公司 */
    OFFICE = 'OFFICE',
    /** 本租户 */
    TENANT = 'TENANT'
}

/**
 * 面板 请求
 */
export interface QueryDashBoardRequest extends PageRequest {
    /** 时间范围: today-今日、thisWeek-本周、thisMonth-本月、thisYear-本年、custom-自定义 */
    timeRange: string
    /** custom-自定义 开始时间 */
    startTime: string | null
    /** custom-自定义 结束时间 */
    endTime: string | null
    /** 数据范围：SELF-本人、DEPT-本部门、OFFICE-本分公司、TENANT-本租户 */
    dataRange: string
    /** 分公司ID（当数据范围为OFFICE且选择了具体分公司时传递）- 使用string类型以支持大整数（雪花ID） */
    officeId: string | null
    /** 部门ID（当数据范围为DEPT且选择了具体部门时传递）- 使用string类型以支持大整数（雪花ID） */
    departmentId: string | null
}

/**
 * 面板 订单任务统计VO
 */
export interface DashBoardOrderTaskVO {
    /** 总订单数 */
    orderTotalCount: number
    /** 总订单数趋势 */
    orderTotalCountChange: number
    /** 在途订单 */
    orderInProgressCount: number
    /** 在途订单趋势 */
    orderInProgressCountChange: number
    /** 待处理任务 */
    taskPendingCount: number
    /** 待处理任务趋势 */
    taskPendingCountChange: number
    /** 逾期任务 */
    taskOverdueCount: number
    /** 逾期任务趋势 */
    taskOverdueCountChange: number
}

/**
 * 面板 实时订单VO
 */
export interface DashBoardRealtimeOrderVO {
    /** 订单编号 */
    orderNo: string
    /** 创建时间 */
    dateCreated: string
    /** 订单来源 */
    orderSource: string
}

/**
 * (面板)订单/任务统计：总订单数、在途订单、待处理任务、逾期任务
 * @param params 查询参数
 */
export const orderTaskStatistics = (params: QueryDashBoardRequest): Promise<DashBoardOrderTaskVO> => {
    return request.post('/ctower/dash-board/orderTaskStatistics', params)
}

/**
 * (面板)实时订单
 * @param params 查询参数
 * @returns 分页列表
 */
export const realtimeOrder = (params: QueryDashBoardRequest): Promise<PageResponse<DashBoardRealtimeOrderVO>> => {
    return request.post('/ctower/dash-board/realtimeOrder', params)
}

/**
 * (面板)实时任务
 * @param params 查询参数
 * @returns 分页列表
 */
export const realtimeTask = (params: QueryDashBoardRequest): Promise<PageResponse<DashBoardTaskVO>> => {
    return request.post('/ctower/dash-board/realtimeTask', params)
}

/**
 * 面板 询价成交趋势VO
 */
export interface DashBoardInquiryDealTrendVO {
    /** 日期刻度（按天：yyyy-MM-dd，按月：yyyy-MM） */
    dates: string[]
    /** 询价数量（与 dates 一一对应） */
    inquiryData: number[]
    /** 成交数量（与 dates 一一对应） */
    dealData: number[]
}

/**
 * (面板)询价成交趋势
 * @param params 查询参数
 * @returns 趋势数据
 */
export const inquiryDealTrend = (params: QueryDashBoardRequest): Promise<DashBoardInquiryDealTrendVO> => {
    return request.post('/ctower/dash-board/inquiryDealTrend', params)
}

/**
 * 面板 客户排行榜VO
 */
export interface DashBoardCustomerRankVO {
    /** 客户ID */
    customerId: string
    /** 订单数量 */
    orderCount: number
    /** 变化量（相比上一周期的订单数量变化） */
    lastPeriodOrderCount: number
}

/**
 * (面板)成交订单量客户排行榜TOP10
 * @param params 查询参数
 * @returns 客户排行榜列表（直接返回数组，非分页）
 */
export const customerRank = (params: QueryDashBoardRequest): Promise<DashBoardCustomerRankVO[]> => {
    return request.post('/ctower/dash-board/customerRank', params)
}

/**
 * 面板 全球订单流向图VO
 */
export interface DashBoardOrderFlowVO {
    /** 装货港代码 */
    pol: string
    /** 卸货港代码 */
    pod: string
    /** 订单数量 */
    orderCount: number
}

/**
 * (面板)全球订单流向图
 * @param params 查询参数
 * @returns 订单流向数据列表
 */
export const orderFlow = (params: QueryDashBoardRequest): Promise<DashBoardOrderFlowVO[]> => {
    return request.post('/ctower/dash-board/orderFlow', params)
}

/**
 * 平台指数VO
 */
export interface PlatformIndexVO {
    /** 指数ID */
    id: string
    /** 指数名称 */
    indexName: string
    /** 中文名称 */
    chineseName: string
    /** 最新指数 */
    indexValue: number
    /** 涨跌幅度(%) */
    changeRate: number
    /** 租户ID */
    tenantId: string
    /** 创建人ID */
    createdBy: string
    /** 创建人姓名 */
    createdByName: string
    /** 创建时间 */
    dateCreated: string
    /** 最后修改人ID */
    modifiedBy: string
    /** 最后修改人姓名 */
    modifiedByName: string
    /** 最后修改时间 */
    dateModified: string
}

/**
 * 获取所有指数
 * @returns 指数列表（不分页,按创建时间倒序排列）
 */
export const getAllIndex = (): Promise<PlatformIndexVO[]> => {
    return request.post('/platform/index/all', {})
}

/**
 * 分公司信息VO
 */
export interface OfficeInfoVO {
    /** 分公司ID */
    officeId: string
    /** 分公司名称 */
    officeName: string
    /** 分公司英文名称 */
    officeNameEn: string
}

/**
 * 部门信息VO
 */
export interface DepartmentInfoVO {
    /** 部门ID */
    departmentId: string
    /** 部门名称 */
    departmentName: string
    /** 部门英文名称 */
    departmentNameEn: string
    /** 所属分公司ID */
    officeId: string
    /** 所属分公司名称 */
    officeName: string
    /** 所属分公司英文名称 */
    officeNameEn: string
}

/**
 * 用户组织架构信息响应VO
 */
export interface UserOrgInfoResponseVO {
    /** 分公司列表 */
    officeList: OfficeInfoVO[]
    /** 部门列表（包含所属公司信息） */
    departmentList: DepartmentInfoVO[]
}

/**
 * 获取用户组织架构信息
 * 根据当前登录用户ID查询其组织架构信息，返回公司列表和部门列表（部门中包含所属公司信息）
 * @returns 用户组织架构信息（包含分公司列表和部门列表）
 */
export const getUserOrgInfo = (): Promise<UserOrgInfoResponseVO> => {
    return request.post('/enterprise/tenant/tenantUser/getUserOrgInfo', {})
}

/**
 * 面板 询价排行榜VO
 */
export interface DashBoardInquiryRankVO {
    /** 起运港ID */
    pol: string
    /** 目的港ID */
    pod: string
    /** 查询次数（当前周期） */
    queryCount: number
    /** 上个周期查询次数 */
    lastPeriodQueryCount: number
}

/**
 * (面板)热门询价排行榜TOP10/港口组合询价排名
 * @param params 查询参数
 * @returns 询价排行榜数据列表
 */
export const inquiryRank = (params: QueryDashBoardRequest): Promise<DashBoardInquiryRankVO[]> => {
    return request.post('/ctower/dash-board/inquiryRank', params)
}

/**
 * 面板 销售报价时效排行榜VO
 */
export interface DashBoardQuotationEfficiencyRankVO {
    /** 用户ID（销售人员ID）- 使用string类型以支持大整数（雪花ID） */
    userId: string
    /** 平均报价效率（小时），值越小表示报价速度越快 */
    avgEfficiencyHours: number
    /** 上个周期的平均报价效率（小时） */
    lastPeriodAvgEfficiencyHours: number
}

/**
 * (面板)销售报价时效排行榜TOP10
 * 按平均报价时长（小时）升序排列，时效越短排名越高
 * @param params 查询参数
 * @returns 销售报价时效排行榜数据列表（非分页，直接返回TOP10数组）
 */
export const quotationEfficiencyRank = (params: QueryDashBoardRequest): Promise<DashBoardQuotationEfficiencyRankVO[]> => {
    return request.post('/ctower/dash-board/quotationEfficiencyRank', params)
}

/**
 * 面板 待处理任务VO
 */
export interface DashBoardTaskVO {
    /** 任务实例ID - 使用string类型以支持大整数（雪花ID） */
    taskInstanceId: string
    /** 订单id */
    orderId: string
    /** 订单号 */
    orderNo: string
    /** 优先级：1-高 2-中 3-低 */
    priority: number
    /** 企业ID - 使用string类型以支持大整数（雪花ID） */
    enterpriseId: string
    /** 任务名称 */
    taskName: string
    /** 计划结束时间 */
    plannedEndTime: string
}

/**
 * (面板)待处理任务
 * 查询面板中的待处理任务列表，支持分页查询，按计划结束时间升序排列
 * @param params 查询参数
 * @returns 分页列表
 */
export const pendingTasks = (params: QueryDashBoardRequest): Promise<PageResponse<DashBoardTaskVO>> => {
    return request.post('/ctower/dash-board/pendingTasks', params)
}

/**
 * (面板)逾期任务
 * @param params 查询参数
 * @returns 分页列表
 */
export const overdueTasks = (params: QueryDashBoardRequest): Promise<PageResponse<DashBoardTaskVO>> => {
    return request.post('/ctower/dash-board/overdueTasks', params)
}
